import { CheckCircle2, Snowflake, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatMoney } from "@/lib/currency";

/**
 * HistoryHeatStrip — the 12-month streak strip on the Streak page. Each
 * cell shows a month's outcome (won / saved / negative / no data) and
 * offers an "undo save" button for saved months (with a 7-day refund
 * window enforced server-side).
 */
export default function HistoryHeatStrip({ data, currency, busy, onRefund }) {
  const { t } = useTranslation();
  return (
    <div className="card-flat p-6 mb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="label-eyebrow">{t("streak_kids.last_12_months")}</div>
        {data.saves_this_year.length > 0 && (
          <div className="text-xs text-[var(--text-muted)]">
            {t("streak_kids.saves_used_this_year")}{" "}
            <span className="font-mono text-[var(--accent-primary)] font-medium">
              {data.tokens_used}/{data.tokens_max}
            </span>
          </div>
        )}
      </div>
      <div className="flex gap-2 flex-wrap">
        {data.history.map((h) => {
          const sign = h.won ? "+" : "";
          const tooltip = h.has_data
            ? (h.saved
                ? t("streak_kids.month_tooltip_saved", { month: h.month_short, year: h.year, sign, amount: formatMoney(h.net, currency) })
                : t("streak_kids.month_tooltip", { month: h.month_short, year: h.year, sign, amount: formatMoney(h.net, currency) }))
            : t("streak_kids.no_data");
          return (
          <div
            key={h.ym || `${h.year}-${h.month}`}
            className="flex flex-col items-center gap-1.5"
            data-testid={`streak-month-${h.year}-${h.month}`}
          >
            <div
              className="h-14 w-14 rounded-lg flex items-center justify-center relative transition-all hover:-translate-y-0.5"
              title={tooltip}
              style={{
                background: !h.has_data
                  ? "var(--bg-accent)"
                  : h.saved
                    ? "linear-gradient(135deg, #86A8D8, #6B8FBB)"
                    : h.won
                      ? "var(--accent-primary)"
                      : "rgba(196,91,77,0.85)",
                color: !h.has_data ? "var(--text-muted)" : "#FDFCFB",
                border: !h.has_data ? "1px dashed var(--border-default)" : "none",
              }}
            >
              {h.has_data
                ? (h.saved
                    ? <Snowflake size={20}/>
                    : h.won
                      ? <CheckCircle2 size={20}/>
                      : <span className="font-mono text-lg">×</span>)
                : <span className="text-xs">—</span>}
              {h.saved && (
                <button
                  className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-white text-[var(--text-secondary)] flex items-center justify-center hover:text-[var(--accent-danger)] shadow"
                  onClick={() => onRefund({ ym: h.ym, month_short: h.month_short, year: h.year, net: h.net })}
                  disabled={busy}
                  title={t("streak_kids.refund_this_save")}
                  data-testid={`refund-${h.ym}`}
                >
                  <X size={11}/>
                </button>
              )}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">{h.month_short}</div>
          </div>
          );
        })}
      </div>
      <div className="mt-4 flex items-center gap-4 flex-wrap text-xs text-[var(--text-muted)]">
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-[var(--accent-primary)]"/> {t("streak_kids.positive_cashflow")}</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm" style={{ background: "linear-gradient(135deg,#86A8D8,#6B8FBB)" }}/> {t("streak_kids.saved_with_token")}</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm" style={{ background: "rgba(196,91,77,0.85)" }}/> {t("streak_kids.negative")}</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm border border-dashed border-[var(--border-default)] bg-[var(--bg-accent)]"/> {t("streak_kids.no_data")}</span>
      </div>
    </div>
  );
}
